import { Download, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useDownloadEmcProductComplianceFileOutputDocument,
  type EmcProductComplianceFileOutputDocument,
  type EmcProductComplianceFileOutputDocumentStatus,
} from '@/shared/hooks/api/emcComplianceFiles'

type EmcProductComplianceFileOutputDocumentsSectionProps = {
  productId: string
  outputDocuments: EmcProductComplianceFileOutputDocument[]
}

function outputDocumentStatusVariant(
  status: EmcProductComplianceFileOutputDocumentStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'COMPLETED') {
    return 'default'
  }
  if (status === 'FAILED') {
    return 'destructive'
  }
  return 'secondary'
}

function formatCreatedAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}

function OutputDocumentActionsCell({
  productId,
  document,
}: {
  productId: string
  document: EmcProductComplianceFileOutputDocument
}) {
  const { t } = useTranslation('common')
  const downloadMutation = useDownloadEmcProductComplianceFileOutputDocument(
    productId
  )

  const isInFlight =
    document.status === 'PENDING' || document.status === 'PROCESSING'

  if (document.status === 'COMPLETED') {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={downloadMutation.isPending}
        onClick={() => {
          void downloadMutation
            .mutateAsync({
              outputDocumentId: document.id,
              fileName: document.fileName,
            })
            .catch(() => {
              toast.error(
                t(
                  'v2.pages.products.detail.emcFiles.outputDocuments.downloadFailed'
                )
              )
            })
        }}
      >
        <Download className="size-3.5" />
        {t('v2.pages.products.detail.emcFiles.outputDocuments.download')}
      </Button>
    )
  }

  if (isInFlight) {
    return (
      <Button type="button" variant="outline" size="sm" disabled>
        <Loader2 className="size-3.5 animate-spin" />
        {t('v2.pages.products.detail.emcFiles.outputDocuments.generating')}
      </Button>
    )
  }

  return <span className="text-sm text-muted-foreground">—</span>
}

export function EmcProductComplianceFileOutputDocumentsSection({
  productId,
  outputDocuments,
}: EmcProductComplianceFileOutputDocumentsSectionProps) {
  const { t, i18n } = useTranslation('common')

  if (outputDocuments.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.products.detail.emcFiles.outputDocuments.empty')}
      </p>
    )
  }

  return (
    <div className='overflow-x-auto'>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            {t(
              'v2.pages.products.detail.emcFiles.outputDocuments.columns.status'
            )}
          </TableHead>
          <TableHead>
            {t(
              'v2.pages.products.detail.emcFiles.outputDocuments.columns.declarationNumber'
            )}
          </TableHead>
          <TableHead>
            {t(
              'v2.pages.products.detail.emcFiles.outputDocuments.columns.createdAt'
            )}
          </TableHead>
          <TableHead className="text-right">
            {t(
              'v2.pages.products.detail.emcFiles.outputDocuments.columns.actions'
            )}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {outputDocuments.map((document) => (
          <TableRow key={document.id}>
            <TableCell>
              <div className="space-y-1">
                <Badge
                  variant={outputDocumentStatusVariant(document.status)}
                  className="inline-flex items-center gap-1.5"
                >
                  {(document.status === 'PENDING' ||
                    document.status === 'PROCESSING') && (
                    <Loader2 className="size-3 animate-spin" />
                  )}
                  {t(
                    `v2.pages.products.detail.emcFiles.outputDocuments.statuses.${document.status}`
                  )}
                </Badge>
                {document.status === 'FAILED' &&
                  document.errorMessage != null &&
                  document.errorMessage.trim() !== '' && (
                    <p className="text-xs text-destructive">
                      {document.errorMessage}
                    </p>
                  )}
              </div>
            </TableCell>
            <TableCell className="font-mono text-xs">
              {document.declarationNumber ?? '—'}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatCreatedAt(document.createdAt, i18n.language)}
            </TableCell>
            <TableCell className="text-right">
              <OutputDocumentActionsCell
                productId={productId}
                document={document}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </div>
  )
}
